import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { BaseApiService } from '../../../../core/base-api.service';
import { ApiResponse } from '../../../api-response';
import { MemberSummary } from '../models/member.model';

/**
 * Per-user admin actions under `/api/v1/users/{id}` — super-admin only.
 * Backs the member-row kebab menu of the tenant Users tab. (The backend also
 * exposes roles/sessions/MFA management there; add wrappers as UI needs them.)
 */
@Injectable({ providedIn: 'root' })
export class UserAdminService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly usersUrl = `${this.coreApiUrl}/api/v1/users`;

  /** Single user, same shape as a member row. */
  getSummary(userId: string): Observable<MemberSummary> {
    return this.http
      .get<ApiResponse<MemberSummary>>(`${this.usersUrl}/${userId}/summary`)
      .pipe(map(response => response.data));
  }

  /** Resend the invite action email. INVITED users only. */
  resendInvite(userId: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.usersUrl}/${userId}/invitations`, null);
  }

  /** Send a reset-password action email. */
  sendResetPasswordEmail(userId: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.usersUrl}/${userId}/reset-password-email`, null);
  }

  /** Kill all sessions (already-issued access tokens live until expiry). */
  signOutEverywhere(userId: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.usersUrl}/${userId}/logout`, null);
  }

  /** Clear a brute-force lock. LOCKED users only. */
  unlock(userId: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.usersUrl}/${userId}/unlock`, null);
  }
}
